import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Kbd, Modal } from '../components/ui';
import { isTypingTarget } from '../lib/hooks';
import { useTaskActions } from '../features/tasks/actions';
import { useUI } from './ui';

export interface Shortcut {
  /** Alternatives, e.g. ['S', '/'] = S or /. A single entry with spaces is a key combo shown as separate keys. */
  keys: string[];
  label: string;
}

export const SHORTCUT_GROUPS: Array<{ title: string; items: Shortcut[] }> = [
  {
    title: 'Create & find',
    items: [
      { keys: ['N'], label: 'New task' },
      { keys: ['Q'], label: 'Quick add (type a task in one line)' },
      { keys: ['S', '/'], label: 'Search tasks' },
    ],
  },
  {
    title: 'Go to',
    items: [
      { keys: ['I'], label: 'Inbox' },
      { keys: ['T'], label: 'Today' },
      { keys: ['O'], label: 'Overdue' },
      { keys: ['F'], label: 'Follow-ups' },
      { keys: ['U'], label: 'Upcoming' },
      { keys: ['A'], label: 'Assigned to me' },
      { keys: ['P'], label: 'Presets' },
      { keys: ['H'], label: 'History' },
    ],
  },
  {
    title: 'Selected task',
    items: [
      { keys: ['E'], label: 'Edit' },
      { keys: ['C'], label: 'Complete' },
      { keys: ['Z'], label: 'Snooze' },
      { keys: ['R'], label: 'Reminders' },
      { keys: ['X'], label: 'Cancel task' },
    ],
  },
  {
    title: 'Lists & dialogs',
    items: [
      { keys: ['↑ ↓'], label: 'Move between tasks' },
      { keys: ['Enter'], label: 'Open task' },
      { keys: ['Space'], label: 'Select for bulk actions' },
      { keys: ['Esc'], label: 'Close dialog / clear selection' },
      { keys: ['?'], label: 'Show this list' },
    ],
  },
];

/** Single-key shortcut per sidebar route, shown as a hint next to the nav item. */
export const NAV_KEYS: Record<string, string> = {
  '/inbox': 'I',
  '/today': 'T',
  '/overdue': 'O',
  '/followups': 'F',
  '/upcoming': 'U',
  '/mine': 'A',
  '/presets': 'P',
  '/history': 'H',
};

const HINTS_KEY = 'taskos.shortcutHints';
const HINTS_EVENT = 'taskos:shortcut-hints';

/** Per-device preference: show key hints in the sidebar (default on). */
export function useShortcutHints(): [boolean, (v: boolean) => void] {
  const read = () => {
    try {
      return localStorage.getItem(HINTS_KEY) !== 'off';
    } catch {
      return true;
    }
  };
  const [on, setOn] = useState(read);
  useEffect(() => {
    const sync = () => setOn(read());
    window.addEventListener(HINTS_EVENT, sync);
    return () => window.removeEventListener(HINTS_EVENT, sync);
  }, []);
  const set = (v: boolean) => {
    try {
      localStorage.setItem(HINTS_KEY, v ? 'on' : 'off');
    } catch {
      /* storage unavailable — keep in-memory only */
    }
    setOn(v);
    window.dispatchEvent(new Event(HINTS_EVENT));
  };
  return [on, set];
}

export function ShortcutKeys({ keys }: { keys: string[] }) {
  return (
    <span className="shortcut-keys">
      {keys.map((alt, i) => (
        <span key={alt} className="shortcut-alt">
          {i > 0 && <span className="muted small">or</span>}
          {alt.split(' ').map((x) => (
            <Kbd key={x}>{x}</Kbd>
          ))}
        </span>
      ))}
    </span>
  );
}

/** All shortcuts, grouped — used in the help dialog and in Settings. */
export function ShortcutList() {
  return (
    <div className="shortcut-groups">
      {SHORTCUT_GROUPS.map((g) => (
        <section key={g.title} className="shortcut-group" aria-label={g.title}>
          <h3 className="shortcut-group-title">{g.title}</h3>
          <ul>
            {g.items.map((s) => (
              <li key={s.label}>
                <span>{s.label}</span>
                <ShortcutKeys keys={s.keys} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Global single-key shortcuts. Never fire while typing in a field, while a modal is open
 * (except Esc handled by the modal) or with modifier keys.
 */
export function useGlobalShortcuts() {
  const nav = useNavigate();
  const ui = useUI();
  const actions = useTaskActions();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('.modal-root')) return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const cur = ui.current && document.activeElement?.closest('[data-task-id]') ? ui.current : (document.querySelector('[data-current-task]') ? ui.current : null);
      const go = (path: string) => {
        e.preventDefault();
        nav(path);
      };
      switch (k) {
        case 'n':
          e.preventDefault();
          ui.openTaskForm({ mode: 'create' });
          break;
        case 'q':
          e.preventDefault();
          ui.focusQuickAdd();
          break;
        case 's':
        case '/':
          e.preventDefault();
          ui.setSearchOpen(true);
          break;
        case '?':
          e.preventDefault();
          ui.setHelpOpen(true);
          break;
        case 't':
          go('/today');
          break;
        case 'o':
          go('/overdue');
          break;
        case 'u':
          go('/upcoming');
          break;
        case 'f':
          go('/followups');
          break;
        case 'a':
          go('/mine');
          break;
        case 'i':
          go('/inbox');
          break;
        case 'p':
          go('/presets');
          break;
        case 'h':
          go('/history');
          break;
        case 'e':
          if (cur?.can.edit) {
            e.preventDefault();
            ui.openTaskForm({ mode: 'edit', taskId: cur.id });
          }
          break;
        case 'r':
          if (cur?.can.edit) {
            e.preventDefault();
            ui.openTaskForm({ mode: 'edit', taskId: cur.id, focusSection: 'reminders' });
          }
          break;
        case 'c':
          if (cur?.can.complete) {
            e.preventDefault();
            void actions.complete(cur);
          }
          break;
        case 'x':
          if (cur?.can.cancel) {
            e.preventDefault();
            void actions.cancel(cur);
          }
          break;
        case 'z':
          if (cur?.can.snooze) {
            e.preventDefault();
            ui.openSnooze(cur);
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav, ui, actions]);
}

export function ShortcutsHelp() {
  const ui = useUI();
  return (
    <Modal open={ui.helpOpen} onClose={() => ui.setHelpOpen(false)} title="⌨ Keyboard shortcuts" size="wide">
      <p className="muted small mb">
        Press a single key anywhere in the app — no Ctrl needed. Shortcuts pause while you are typing in a field. Open this list any time with <Kbd>?</Kbd>.
      </p>
      <ShortcutList />
    </Modal>
  );
}
